/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import cx from "classnames";
import React, { useContext, useMemo, useState, useEffect } from "react";
import { useToasts } from "react-toast-notifications";
import { CSSTransition } from "react-transition-group";
import { ExportListContext, ViewTabIds } from "..";
import styles from "./style.module.css";

const Actions = { DOWNLOAD: 1, MARK: 2, REGENERATE: 3 };

function Topbar({ activeTab, onBatchRegenerate, onMarkDownloaded, onBatchDownload }) {
  const [curAction, setCurAction] = useState(null);
  const { addToast } = useToasts();
  const { checkedItems, setCheckedItems } = useContext(ExportListContext);
  const isVisible = useMemo(() => activeTab === ViewTabIds.DOWNLOAD, [activeTab]);
  const isActive = useMemo(() => checkedItems.length && !curAction, [checkedItems, curAction]);

  useEffect(() => {
    !curAction && setCheckedItems([]);
  }, [curAction]);

  const onDownloadClick = async () => {
    try {
      setCurAction(Actions.DOWNLOAD);
      addToast("Preparing Download.", { appearance: "success" });
      await onBatchDownload();
    } finally {
      setCurAction(null);
    }
  };

  const onMarkDownloadedClick = async () => {
    try {
      setCurAction(Actions.MARK);
      await onMarkDownloaded();
    } finally {
      setCurAction(null);
    }
  };

  const onRegenerateClick = async () => {
    try {
      setCurAction(Actions.REGENERATE);
      await onBatchRegenerate();
      addToast("eReports successfully queued for regeneration.", { appearance: "success" });
    } finally {
      setCurAction(null);
    }
  };

  return (
    <CSSTransition
      in={isVisible}
      timeout={{ exit: 300 }}
      unmountOnExit={true}
      classNames={{ enter: styles.revealEnter, exit: styles.revealExit }}
    >
      <div className={cx(styles.elTopbar, styles.reveal)}>
        <button type="button" className="btn btnSuccess" disabled={!isActive} onClick={onDownloadClick}>
          Batch Download&nbsp;
          {curAction == Actions.DOWNLOAD && (
            <span id="results-loading-icon" className="saving-white-ico icon-64" />
          )}
        </button>

        <button
          type="button"
          className={cx(styles.btn, "btn", "btn-light")}
          disabled={!isActive}
          onClick={onMarkDownloadedClick}
        >
          Batch Mark as Downloaded&nbsp;
          {curAction == Actions.MARK && (
            <span id="results-loading-icon-2" className="saving-white-ico icon-64" />
          )}
        </button>

        <button type="button" className="btn btnTeal" disabled={!isActive} onClick={onRegenerateClick}>
          Batch Regenerate&nbsp;
          {curAction == Actions.REGENERATE && (
            <span id="results-loading-icon-3" className="saving-white-ico icon-64" />
          )}
        </button>
      </div>
    </CSSTransition>
  );
}

export default Topbar;
